import { adminApi } from './admin-api';
import {
  normalizeAdminExecution,
  normalizeAdminExecutionsPage,
} from '@/features/automations/utils/admin-execution-normalizer';
import type {
  AdminFilters,
  AdminExecution,
  AdminExecutionDetail,
  AutomationEffectAnomaly,
  QueueStats,
  PaginatedResponse,
} from '@/types/admin.types';

export const adminAutomationsService = {
  getExecutions: async (filters: AdminFilters = {}): Promise<PaginatedResponse<AdminExecution>> => {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.search) params.set('search', filters.search);
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    const response = await adminApi.get(`/admin/automations/executions?${params.toString()}`);
    return normalizeAdminExecutionsPage(response.data);
  },

  getExecutionById: async (uuid: string): Promise<AdminExecutionDetail> => {
    const response = await adminApi.get(`/admin/automations/executions/${uuid}`);
    return normalizeAdminExecution(response.data);
  },

  retryExecution: async (uuid: string): Promise<{ message: string }> => {
    const response = await adminApi.post(`/admin/automations/executions/${uuid}/retry`);
    return response.data;
  },

  cancelExecution: async (uuid: string): Promise<{ message: string }> => {
    const response = await adminApi.post(`/admin/automations/executions/${uuid}/cancel`);
    return response.data;
  },

  getQueues: async (): Promise<QueueStats[]> => {
    const response = await adminApi.get('/admin/automations/queues');
    return response.data;
  },

  pauseQueue: async (name: string): Promise<{ message: string }> => {
    const response = await adminApi.post(`/admin/automations/queues/${name}/pause`);
    return response.data;
  },

  resumeQueue: async (name: string): Promise<{ message: string }> => {
    const response = await adminApi.post(`/admin/automations/queues/${name}/resume`);
    return response.data;
  },

  drainQueue: async (name: string): Promise<{ message: string }> => {
    const response = await adminApi.post(`/admin/automations/queues/${name}/drain`);
    return response.data;
  },

  getStats: async (period: string = '7d'): Promise<Record<string, unknown>> => {
    const response = await adminApi.get(`/admin/automations/stats?period=${period}`);
    return response.data;
  },

  getEffectAnomalies: async (limit = 10): Promise<AutomationEffectAnomaly[]> => {
    const response = await adminApi.get(`/admin/automations/effect-anomalies?limit=${limit}`);
    return response.data;
  },
};
